// The new wallet deposit, balance and swap flow accepts production wallets only.
// Crossmint staging wallets must never be shown as mainnet deposit addresses.
export const CROSSMINT_ENVIRONMENT = String(import.meta.env.VITE_CROSSMINT_CLIENT_ID || "").startsWith("ck_staging_")
  ? "staging"
  : String(import.meta.env.VITE_CROSSMINT_CLIENT_ID || "").startsWith("ck_production_")
    ? "production"
    : "unknown";

const MAINNET_CHAINS = {
  base: { label: "Base", family: "evm" },
  bsc: { label: "BNB Chain", family: "evm" },
  arbitrum: { label: "Arbitrum One", family: "evm" },
  polygon: { label: "Polygon", family: "evm" },
  solana: { label: "Solana", family: "solana" },
};

export const DEPOSIT_TOKENS = [
  { symbol: "USDT", name: "Tether" },
  { symbol: "USDC", name: "USD Coin" },
  { symbol: "ETH", name: "Ether" },
  { symbol: "POL", name: "Polygon" },
  { symbol: "BNB", name: "BNB" },
  { symbol: "SOL", name: "Solana" },
];

const isAddress = (chain, value) =>
  chain === "solana"
    ? /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(value)
    : /^0x[a-fA-F0-9]{40}$/.test(value);

// Optional pairs must use the exact verified mainnet contract/mint.
const readPairs = () => {
  if (CROSSMINT_ENVIRONMENT !== "production") return [];
  let configuredPairs = [];
  try {
    const raw = import.meta.env.VITE_CROSSMINT_DEPOSIT_PAIRS;
    const parsed = JSON.parse(raw || "[]");
    if (Array.isArray(parsed)) configuredPairs = parsed;
  } catch {
    // Keep built-in verified pairs available when optional config is invalid.
  }
  try {
    const mainnetPairs = [
      { token: "ETH", chain: "base", native: true },
      { token: "USDC", chain: "base", address: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913" },
      { token: "USDT", chain: "base", address: "0xfde4c96c8593536e31f229ea8f37b2ada2699bb2" },
      { token: "ETH", chain: "arbitrum", native: true },
      { token: "USDC", chain: "arbitrum", address: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831" },
      { token: "USDT", chain: "arbitrum", address: "0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9" },
      { token: "BNB", chain: "bsc", native: true },
      { token: "USDC", chain: "bsc", address: "0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d" },
      { token: "USDT", chain: "bsc", address: "0x55d398326f99059ff775485246999027b3197955" },
      { token: "POL", chain: "polygon", native: true },
      { token: "USDC", chain: "polygon", address: "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359" },
      { token: "USDT", chain: "polygon", address: "0xc2132d05d31c914a87c6611c10748aeb04b58e8f" },
      { token: "SOL", chain: "solana", native: true },
      { token: "USDC", chain: "solana", address: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v" },
      { token: "USDT", chain: "solana", address: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB" },
    ];
    const chains = MAINNET_CHAINS;
    const defaults = mainnetPairs;
    // Allow a configured minimum for built-in pairs without replacing their
    // verified token contract or native-asset definition.
    const configuredMinimums = new Map(configuredPairs
      .filter((item) => item?.minDeposit != null)
      .map((item) => [`${String(item.token || "").toUpperCase()}:${String(item.chain || "").toLowerCase()}`, item.minDeposit]));
    // Optional env input may adjust minimums; it cannot enable a token that
    // the backend swap execution allowlist does not support.
    const pairs = defaults.flatMap((item) => {
      const token = String(item?.token || "").toUpperCase();
      const chain = String(item?.chain || "").toLowerCase();
      const address = String(item?.address || "");
      const native = item?.native === true;
      const validNative =
        (token === "BNB" && chain === "bsc") ||
        (token === "SOL" && chain === "solana") ||
        (token === "POL" && chain === "polygon") ||
        (token === "ETH" && ["arbitrum", "base"].includes(chain));
      if (!chains[chain] || !DEPOSIT_TOKENS.some((t) => t.symbol === token)) return [];
      // The installed wallet SDK does not include Ethereum mainnet. Native
      // Bitcoin and Tron are not supported by this wallet integration.
      if (native ? !validNative : !["USDT", "USDC"].includes(token) || !isAddress(chain, address)) return [];
      const configuredMinDeposit = configuredMinimums.get(`${token}:${chain}`);
      const minDeposit = Number(configuredMinDeposit);
      const decimals = token === "SOL" ? 9 : chain === "bsc" && ["USDC", "USDT"].includes(token) ? 18 : ["USDC", "USDT"].includes(token) ? 6 : 18;
      return [{ token, chain, address: native ? null : address, native, decimals, minDeposit: configuredMinDeposit != null && Number.isFinite(minDeposit) && minDeposit > 0 ? minDeposit : null, ...chains[chain] }];
    });
    return pairs.filter((pair, index) =>
      pairs.findIndex((other) => other.token === pair.token && other.chain === pair.chain) === index,
    );
  } catch {
    return [];
  }
};

export const CROSSMINT_WALLET_ASSET_PAIRS = readPairs();
// Solana SPL assets remain visible in balances/withdrawal for existing funds.
// They are hidden from new deposits until the SPL Relay execution path is ready.
export const CROSSMINT_DEPOSIT_PAIRS = CROSSMINT_WALLET_ASSET_PAIRS.filter((pair) =>
  !(pair.chain === "solana" && !pair.native),
);
